import { useState, useCallback, useMemo, useRef } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { companiesApi } from '../services/api/companies';
import { isEmail, isPositiveNumber, required } from '../utils/validators';
import { EQUIPMENT_TYPES } from '../components/admin/carrierCapabilities';
import { COUNTRIES, getCountryDisplayName } from '../data/countries';
import type { CarrierImportReport } from '../types/companies';

// Carrier Status (issue #173) is the Carrier Directory source of truth.
// The list below mirrors TransportationCompany::CARRIER_STATUSES on the
// backend. Hook and component code reads from this constant instead of
// the legacy `active` boolean.
export const CARRIER_STATUSES = [
  'pending_validation',
  'contacted',
  'waiting',
  'active',
  'inactive',
  'not_interested',
] as const;

export type CarrierStatus = (typeof CARRIER_STATUSES)[number];

export const CARRIER_STATUS_FILTER_ALL = 'all';
// The directory status filter offers every established Carrier Status
// plus the "all" sentinel, so an admin can segment the list by any
// status (#284). Derived from CARRIER_STATUSES so the filter and the
// form-editing vocabulary cannot drift apart.
export const CARRIER_STATUS_FILTER_OPTIONS = CARRIER_STATUSES;

export type CarrierStatusFilter = (typeof CARRIER_STATUS_FILTER_OPTIONS)[number] | typeof CARRIER_STATUS_FILTER_ALL;

export interface TransportationCompany {
  id: number;
  name: string;
  // Email is optional for Carrier Directory entries (issue #174). The
  // backend tolerates `null` so manually-created or imported carriers
  // without an email on file can still be saved to the directory.
  email?: string;
  address?: string;
  phone_number?: string;
  country: string;
  max_weight?: number;
  max_length?: number;
  max_width?: number;
  max_height?: number;
  delivery_countries: string[];
  equipment_types: string[];
  carrier_status: CarrierStatus;
  // Carrier Status is the source of truth for both the visible badge
  // and the listing filter (#173). `carrier_status_label` is the
  // admin-facing localized label resolved by the backend against the
  // request locale. Imported and manually-created carriers share this
  // shape (#179), so the list and details UI can render either kind
  // of record without branching on provenance.
  carrier_status_label: string;
  // Legacy boolean kept for backward compatibility with any client
  // that has not migrated (#173). The server derives it from
  // `carrier_status === "active"`, and the admin UI does not read it
  // — list/filter/edit flows consume `carrier_status` instead.
  active: boolean;
  // Carrier Directory profile fields (issue #174). Mirrors the PRD
  // spreadsheet fields so manually-created and imported carriers can
  // carry the same directory detail that operators maintain by hand.
  postal_code?: string;
  city?: string;
  website?: string;
  direct_contact?: string;
  validated_at?: string | null;
  validated_by?: string | null;
  notes?: string;
  other_equipment?: string;
}

type CompanyFieldErrors = {
  name: string[];
  email: string[];
  country: string[];
  max_weight: string[];
  max_length: string[];
  max_width: string[];
  max_height: string[];
};

type CompanyValidationResult = {
  fieldErrors: CompanyFieldErrors;
  formErrors: string[];
};

const EMPTY_FIELD_ERRORS = (): CompanyFieldErrors => ({
  name: [],
  email: [],
  country: [],
  max_weight: [],
  max_length: [],
  max_width: [],
  max_height: [],
});

const COMPANY_VALIDATION_KEYS = {
  nameRequired: 'admin.companies.validation.name_required',
  // Email is optional for Carrier Directory entries (issue #174). The
  // format check (emailInvalid) still runs when an email is provided;
  // the legacy emailRequired key is intentionally dropped.
  emailInvalid: 'admin.companies.validation.email_invalid',
  countryRequired: 'admin.companies.validation.country_required',
  positiveNumber: 'admin.companies.validation.positive_number',
} as const;

const ADMIN_COMPANIES_KEY = ['admin', 'companies'] as const;

// Country identity helpers (#302). Directory rows may store either the
// canonical ISO alpha2 code (the new shape, shared with the homepage) or a
// legacy full country name. Both resolve to the same canonical identity so
// filters match across shapes, while a value that resolves to nothing is
// preserved verbatim rather than dropped.
export interface CountryFilterOption {
  value: string;
  label: string;
}

// Legacy country labels the backend already canonicalizes (#302): stored
// rows may still hold "UK" or "Czech Republic" from before the migration.
// They resolve to the same identity as their canonical entry so filters
// and display stay consistent with the backend. Keep in sync with the
// backend catalog; do not grow this into a second country catalog.
const LEGACY_COUNTRY_ALIASES: Record<string, string> = {
  uk: 'GB',
  'czech republic': 'CZ',
};

const findCountryByValue = (value: string | null | undefined) => {
  const needle = (value ?? '').trim().toLowerCase();
  if (!needle) {
    return undefined;
  }

  const aliasCode = LEGACY_COUNTRY_ALIASES[needle];
  if (aliasCode) {
    return COUNTRIES.find((country) => country.alpha2 === aliasCode);
  }

  return COUNTRIES.find(
    (country) =>
      country.alpha2.toLowerCase() === needle ||
      country.name.toLowerCase() === needle,
  );
};

export const resolveCountryName = (value: string, language: string = ''): string => {
  const country = findCountryByValue(value);

  return country ? getCountryDisplayName(country, language) : value;
};

// Canonical filter key: the ISO alpha2 code when the value resolves to a
// known country, otherwise the raw value (the visible legacy fallback).
const countryFilterKey = (value: string): string => {
  const country = findCountryByValue(value);

  return country ? country.alpha2 : value;
};

// Build the origin/destination filter options. Values collapse to their
// canonical alpha2 code so a code row and a legacy-name row share a single
// option, labels are the readable country name, and unresolved legacy
// values keep their own option so nothing is silently omitted.
const buildCountryFilterOptions = (rawValues: string[], language: string = ''): CountryFilterOption[] => {
  const byKey = new Map<string, CountryFilterOption>();

  rawValues.forEach((raw) => {
    if (!raw) {
      return;
    }

    const key = countryFilterKey(raw);
    if (!byKey.has(key)) {
      byKey.set(key, { value: key, label: resolveCountryName(raw, language) });
    }
  });

  return Array.from(byKey.values()).sort((a, b) => a.label.localeCompare(b.label));
};

export interface UseCompaniesListReturn {
  companies: TransportationCompany[];
  loading: boolean;
  statusFilter: string;
  setStatusFilter: (value: string) => void;
  searchQuery: string;
  setSearchQuery: (value: string) => void;
  originCountryFilter: string;
  setOriginCountryFilter: (value: string) => void;
  destinationCountryFilter: string;
  setDestinationCountryFilter: (value: string) => void;
  currentPage: number;
  setCurrentPage: React.Dispatch<React.SetStateAction<number>>;
  itemsPerPage: number;
  setItemsPerPage: React.Dispatch<React.SetStateAction<number>>;
  filteredCompanies: TransportationCompany[];
  paginatedItems: TransportationCompany[];
  totalPages: number;
  countries: CountryFilterOption[];
  allDeliveryCountries: CountryFilterOption[];
  isModalOpen: boolean;
  setIsModalOpen: (open: boolean) => void;
  editingCompany: TransportationCompany | null;
  formData: Partial<TransportationCompany>;
  setFormData: React.Dispatch<React.SetStateAction<Partial<TransportationCompany>>>;
  submitting: boolean;
  viewingCompany: TransportationCompany | null;
  isViewModalOpen: boolean;
  setIsViewModalOpen: (open: boolean) => void;
  handleOpenModal: (company?: TransportationCompany) => Promise<void>;
  handleViewDetails: (company: TransportationCompany) => void;
  handleSubmit: () => Promise<void>;
  handleMultiSelectChange: (field: 'delivery_countries' | 'equipment_types', value: string) => void;
  equipmentTypes: string[];
  carrierStatuses: readonly CarrierStatus[];
  carrierStatusFilterOptions: readonly CarrierStatusFilter[];
  fieldErrors: CompanyFieldErrors;
  formErrors: string[];
  validate: () => CompanyValidationResult;
  // Carrier Directory import (issue #178). The hook owns the import
  // modal state, the selected file, the row-level report, and the
  // download/import mutations. The component receives everything via
  // props and only calls these handlers — no business logic in JSX.
  isImportModalOpen: boolean;
  setIsImportModalOpen: (open: boolean) => void;
  importFile: File | null;
  importReport: CarrierImportReport | null;
  importError: string | null;
  importSubmitting: boolean;
  templateDownloading: boolean;
  guideDownloading: boolean;
  handleOpenImportModal: () => void;
  handleCloseImportModal: () => void;
  handleImportFileChange: (file: File | null) => void;
  handleDownloadTemplate: () => Promise<void>;
  handleDownloadGuide: () => Promise<void>;
  handleSubmitImport: () => Promise<void>;
}

export const useCompaniesList = (): UseCompaniesListReturn => {
  const { t, i18n } = useTranslation();
  const language = i18n.resolvedLanguage || i18n.language;
  const queryClient = useQueryClient();

  // ---- Server state: companies list (TanStack Query) ------------------------

  const { data, isPending } = useQuery({
    queryKey: ADMIN_COMPANIES_KEY,
    queryFn: () => companiesApi.getAdminTransportationCompanies(),
    staleTime: 30_000,
  });

  const companies = useMemo(
    () => (data?.transportation_companies || []) as TransportationCompany[],
    [data?.transportation_companies],
  );

  // ---- Mutations (TanStack Query) -------------------------------------------

  const createMutation = useMutation({
    mutationFn: (form: Partial<TransportationCompany>) =>
      companiesApi.createTransportationCompany(form),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_COMPANIES_KEY });
      toast.success(t('admin.companies.created'));
      setModalOpen(false);
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : t('admin.companies.createFailed'),
      );
    },
  });

  const updateMutation = useMutation({
    mutationFn: ({ id, data }: { id: number; data: Partial<TransportationCompany> }) =>
      companiesApi.updateTransportationCompany(id, data),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ADMIN_COMPANIES_KEY });
      toast.success(t('admin.companies.updated'));
      setModalOpen(false);
    },
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : t('admin.companies.updateFailed'),
      );
    },
  });

  // Carrier Directory template (issues #176, #178). A focused
  // mutation that streams the backend-owned CSV template to the
  // browser. We track pending state on the mutation rather than a
  // local boolean so the modal can disable the Download button while
  // the request is in flight.
  const downloadTemplateMutation = useMutation({
    mutationFn: () => companiesApi.downloadCarrierTemplate(),
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : t('admin.companies.importTemplateDownloadFailed'),
      );
    },
  });

  // Carrier Directory import guide (PDF). A focused mutation that
  // streams the backend-owned PDF guide to the browser, mirroring
  // the template mutation's pending/error shape. The error key is
  // distinct so the modal can show a guide-specific fallback
  // message.
  const downloadGuideMutation = useMutation({
    mutationFn: () => companiesApi.downloadCarrierGuide(),
    onError: (err) => {
      toast.error(
        err instanceof Error
          ? err.message
          : t('admin.companies.importGuideDownloadFailed'),
      );
    },
  });

  // Carrier Directory import (issue #178). The mutation is the
  // single source of truth for the import submission: the report is
  // stashed in component state via `onSuccess` so the modal can
  // render the row-level feedback immediately, and the list cache is
  // invalidated so newly-created carriers are visible right away
  // (per the #178 acceptance criteria). The import response always
  // carries a row report, so the modal reports it inline; only
  // pre-flight rejections (e.g. non-CSV uploads) bubble through
  // `onError` as a banner.
  const importMutation = useMutation({
    mutationFn: (file: File) => companiesApi.importCarriers(file),
    onSuccess: (report) => {
      setImportReport(report);
      setImportError(null);
      // The list is invalidated on success so the post-import UI
      // re-renders the freshly-created carriers in the table without
      // the admin having to refresh the page.
      queryClient.invalidateQueries({ queryKey: ADMIN_COMPANIES_KEY });
    },
    onError: (err) => {
      setImportReport(null);
      setImportError(
        err instanceof Error
          ? err.message
          : t('admin.companies.importFailed'),
      );
    },
  });

  // ---- Client state (filters, pagination, modal) ----------------------------

  // The Carrier Directory opens filtered to Active carriers (#284). This is
  // an admin-page initial-filter policy only: the admin can switch to any
  // status or All during normal interaction and that choice is honored (the
  // state is never reset to Active on re-render).
  const [statusFilter, setStatusFilter] = useState<string>('active');
  const [searchQuery, setSearchQuery] = useState('');
  const [originCountryFilter, setOriginCountryFilter] = useState('');
  const [destinationCountryFilter, setDestinationCountryFilter] = useState('');

  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<TransportationCompany | null>(null);
  const [formData, setFormData] = useState<Partial<TransportationCompany>>({});
  const [fieldErrors, setFieldErrors] = useState<CompanyFieldErrors>(EMPTY_FIELD_ERRORS);
  const [formErrors, setFormErrors] = useState<string[]>([]);

  const [viewingCompany, setViewingCompany] = useState<TransportationCompany | null>(null);
  const [isViewModalOpen, setIsViewModalOpen] = useState(false);

  // Detail-hydration request guards (#278). The list endpoint omits the
  // carrier capacity/address fields, so both the View modal and the Edit
  // form top up their data from the singular detail endpoint. These
  // monotonically increasing counters let a later click drop the response
  // of an earlier one when the admin opens rows in quick succession.
  const viewRequestIdRef = useRef(0);
  const editRequestIdRef = useRef(0);

  // Carrier Directory import (issue #178). The modal owns its own
  // open state, the selected file, and the row-level report returned
  // by the backend. `importError` is reserved for pre-flight
  // rejections (non-CSV uploads, network failures) that arrive
  // without a row report.
  const [isImportModalOpen, setIsImportModalOpen] = useState(false);
  const [importFile, setImportFile] = useState<File | null>(null);
  const [importReport, setImportReport] = useState<CarrierImportReport | null>(null);
  const [importError, setImportError] = useState<string | null>(null);

  const setModalOpen = useCallback((open: boolean) => {
    setIsModalOpen(open);

    if (!open) {
      setFieldErrors(EMPTY_FIELD_ERRORS());
      setFormErrors([]);
    }
  }, []);

  // Reset to page 1 when filters change
  const prevFilterKey = useMemo(
    () => `${statusFilter}|${searchQuery}|${originCountryFilter}|${destinationCountryFilter}`,
    [statusFilter, searchQuery, originCountryFilter, destinationCountryFilter],
  );
  // Using a ref-like assignment via a separate counter is fragile; use effect instead
  const [filterKey, setFilterKey] = useState(prevFilterKey);
  if (filterKey !== prevFilterKey) {
    setFilterKey(prevFilterKey);
    setCurrentPage(1);
  }

  // ---- Derived data ---------------------------------------------------------

  const filteredCompanies = useMemo(() => {
    return companies.filter(company => {
      const matchesSearch = company.name?.toLowerCase().includes(searchQuery.toLowerCase()) ?? true;
      const matchesStatus = statusFilter === CARRIER_STATUS_FILTER_ALL
        ? true
        : company.carrier_status === statusFilter;
      const matchesOrigin = originCountryFilter
        ? countryFilterKey(company.country) === originCountryFilter
        : true;
      const matchesDestination = destinationCountryFilter
        ? company.delivery_countries.some(
            (country) => countryFilterKey(country) === destinationCountryFilter,
          )
        : true;

      return matchesSearch && matchesStatus && matchesOrigin && matchesDestination;
    });
  }, [companies, statusFilter, searchQuery, originCountryFilter, destinationCountryFilter]);

  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedItems = useMemo(
    () => filteredCompanies.slice(startIndex, endIndex),
    [filteredCompanies, startIndex, endIndex]
  );
  const totalPages = Math.ceil(filteredCompanies.length / itemsPerPage);

  const countries = useMemo(
    () => buildCountryFilterOptions(companies.map(c => c.country), language),
    [companies, language]
  );

  const allDeliveryCountries = useMemo(
    () => buildCountryFilterOptions(companies.flatMap(c => c.delivery_countries), language),
    [companies, language]
  );

  // ---- Handlers -------------------------------------------------------------

  const submitting = createMutation.isPending || updateMutation.isPending;

  const handleOpenModal = useCallback(async (company?: TransportationCompany) => {
    if (company) {
      // Edit (#278): the list endpoint omits the capacity fields
      // (max_weight/max_length/max_width/max_height) and the address, so
      // the list row alone cannot seed an editable form. Fetch the
      // singular detail FIRST and only initialise/open the form once it
      // resolves — that guarantees a late response can never overwrite
      // values the admin has already started typing. The request id drops
      // the response of a previously-clicked row if the admin opens
      // another row before this one settles.
      const requestId = ++editRequestIdRef.current;

      let hydrated: TransportationCompany | null = null;
      try {
        const response = await companiesApi.getAdminTransportationCompany(company.id);
        const detail = response?.transportation_company as
          | Partial<TransportationCompany>
          | undefined;
        // Only a detail response that carries the carrier identity is
        // usable; an empty or malformed payload stays `null` and is
        // rejected below rather than falling back to the incomplete list
        // row.
        if (detail && detail.id !== undefined) {
          hydrated = { ...company, ...detail };
        }
      } catch (err) {
        if (requestId !== editRequestIdRef.current) {
          return;
        }
        toast.error(
          err instanceof Error ? err.message : t('common.errors.generic'),
        );
        return;
      }

      if (requestId !== editRequestIdRef.current) {
        return;
      }

      if (!hydrated) {
        // Missing/invalid detail (#278 review): never open an editable
        // form seeded only with the incomplete list row — submitting it
        // would PATCH blank max_* values and wipe the carrier's stored
        // capacity. Surface the failure through the existing toast
        // convention and leave the form closed.
        toast.error(t('common.errors.generic'));
        return;
      }

      setEditingCompany(hydrated);
      setFormData({
        ...hydrated,
        equipment_types: (hydrated.equipment_types ?? []).filter(
          (type) => EQUIPMENT_TYPES.includes(type),
        ),
      });
      setFieldErrors(EMPTY_FIELD_ERRORS());
      setFormErrors([]);
      setModalOpen(true);
      return;
    }

    // Create (#278 review): invalidate any in-flight edit hydration so a
    // late detail response cannot open the edit form over the newly
    // opened Create form.
    editRequestIdRef.current += 1;

    setEditingCompany(null);
    setFormData({
      name: '',
      // Email is optional for Carrier Directory entries (#174). New
      // carrier forms leave it blank; admins can fill it in if known.
      email: '',
      address: '',
      phone_number: '',
      country: '',
      // Carrier Directory profile fields (#174). All optional.
      postal_code: '',
      city: '',
      website: '',
      direct_contact: '',
      // `validated_at` is a free-form ISO string from the datetime
      // input. We leave it empty for new carriers; admins record the
      // validation date at the moment they confirm a carrier.
      validated_at: '',
      notes: '',
      other_equipment: '',
      delivery_countries: [],
      equipment_types: [],
      // Manually-created carriers default to pending_validation (#173).
      // The admin explicitly opts the carrier in via the status select
      // before it is ever considered for quote matching.
      carrier_status: 'pending_validation'
    });
    setFieldErrors(EMPTY_FIELD_ERRORS());
    setFormErrors([]);
    setModalOpen(true);
  }, [setModalOpen, t]);

  const handleViewDetails = useCallback((company: TransportationCompany) => {
    // View (#278): open immediately with the list row so the modal stays
    // responsive, then hydrate the capacity/address fields from the
    // singular endpoint. The request id drops a stale response when the
    // admin clicks View on another row before this one settles, and the
    // id check inside the updater guards a mismatch as a second line.
    setViewingCompany(company);
    setIsViewModalOpen(true);

    const requestId = ++viewRequestIdRef.current;
    void (async () => {
      try {
        const response = await companiesApi.getAdminTransportationCompany(company.id);
        if (requestId !== viewRequestIdRef.current) {
          return;
        }
        const detail = response?.transportation_company as
          | Partial<TransportationCompany>
          | undefined;
        if (!detail) {
          return;
        }
        setViewingCompany((prev) =>
          prev && prev.id === company.id ? { ...prev, ...detail } : prev,
        );
      } catch (err) {
        if (requestId !== viewRequestIdRef.current) {
          return;
        }
        // Keep the list-row data rendered (the modal is read-only) but
        // surface the failure through the existing toast convention so a
        // missing capacity section is never silent.
        toast.error(
          err instanceof Error ? err.message : t('common.errors.generic'),
        );
      }
    })();
  }, [t]);

  const validate = useCallback((): CompanyValidationResult => {
    const nextFieldErrors = EMPTY_FIELD_ERRORS();
    const nextFormErrors: string[] = [];

    const pushError = (field: keyof CompanyFieldErrors, errorKey: string) => {
      nextFieldErrors[field].push(errorKey);
      nextFormErrors.push(errorKey);
    };

    const nameError = required(formData.name ?? '', COMPANY_VALIDATION_KEYS.nameRequired);
    if (nameError) {
      pushError('name', nameError);
    }

    // Email is optional for Carrier Directory entries (issue #174).
    // When the admin does provide one, it must still be a well-formed
    // address — that's the only email check we keep at the form level.
    if ((formData.email ?? '').trim().length > 0) {
      const emailError = isEmail(formData.email ?? '', COMPANY_VALIDATION_KEYS.emailInvalid);
      if (emailError) {
        pushError('email', emailError);
      }
    }

    const countryError = required(formData.country ?? '', COMPANY_VALIDATION_KEYS.countryRequired);
    if (countryError) {
      pushError('country', countryError);
    }

    ([
      'max_weight',
      'max_length',
      'max_width',
      'max_height',
    ] as const).forEach((field) => {
      const value = formData[field];

      if (value === undefined || value === null) {
        return;
      }

      const numberError = isPositiveNumber(value, COMPANY_VALIDATION_KEYS.positiveNumber);
      if (numberError) {
        pushError(field, numberError);
      }
    });

    setFieldErrors(nextFieldErrors);
    setFormErrors(nextFormErrors);

    return {
      fieldErrors: nextFieldErrors,
      formErrors: nextFormErrors,
    };
  }, [formData]);

  const handleSubmit = useCallback(async () => {
    const validation = validate();

    if (validation.formErrors.length > 0) {
      return;
    }

    if (editingCompany) {
      await updateMutation.mutateAsync({ id: editingCompany.id, data: formData });
    } else {
      await createMutation.mutateAsync(formData);
    }
  }, [editingCompany, formData, createMutation, updateMutation, validate]);

  const handleMultiSelectChange = useCallback(
    (field: 'delivery_countries' | 'equipment_types', value: string) => {
      setFormData((prev) => {
        const current = prev[field] || [];
        if (current.includes(value)) {
          return { ...prev, [field]: current.filter(item => item !== value) };
        }
        return { ...prev, [field]: [...current, value] };
      });
    },
    []
  );

  // Carrier Directory import (issue #178) — open the modal in a clean
  // state. Any prior file, report, or pre-flight error is cleared so a
  // fresh import flow never inherits stale state from a previous
  // upload. The modal clears the native file input when this state is
  // reset so the browser can re-select the same file on a new attempt.
  const handleOpenImportModal = useCallback(() => {
    setImportFile(null);
    setImportReport(null);
    setImportError(null);
    setIsImportModalOpen(true);
  }, []);

  const setImportModalOpen = useCallback((open: boolean) => {
    if (open) {
      setIsImportModalOpen(true);
      return;
    }

    setIsImportModalOpen(false);
  }, []);

  const handleCloseImportModal = useCallback(() => {
    setIsImportModalOpen(false);
  }, []);

  const handleImportFileChange = useCallback((file: File | null) => {
    setImportFile(file);
    // Selecting a file discards any prior report/error so the modal
    // reverts to a pre-submit state. The next submit will overwrite
    // these again.
    setImportReport(null);
    setImportError(null);
  }, []);

  // The Download button is a thin wrapper around the template
  // mutation — the actual download (file blob, object URL, anchor
  // click) lives in the ApiClient, mirroring the existing quote
  // attachment downloader.
  const handleDownloadTemplate = useCallback(async () => {
    await downloadTemplateMutation.mutateAsync();
  }, [downloadTemplateMutation]);

  // The Guide Download button mirrors handleDownloadTemplate —
  // the actual download (PDF blob, object URL, anchor click) lives
  // in the ApiClient.
  const handleDownloadGuide = useCallback(async () => {
    await downloadGuideMutation.mutateAsync();
  }, [downloadGuideMutation]);

  const handleSubmitImport = useCallback(async () => {
    if (!importFile) {
      return;
    }
    await importMutation.mutateAsync(importFile);
  }, [importFile, importMutation]);

  return {
    companies,
    loading: isPending,
    statusFilter,
    setStatusFilter,
    searchQuery,
    setSearchQuery,
    originCountryFilter,
    setOriginCountryFilter,
    destinationCountryFilter,
    setDestinationCountryFilter,
    currentPage,
    setCurrentPage,
    itemsPerPage,
    setItemsPerPage,
    filteredCompanies,
    paginatedItems,
    totalPages,
    countries,
    allDeliveryCountries,
    isModalOpen,
    setIsModalOpen: setModalOpen,
    editingCompany,
    formData,
    setFormData,
    submitting,
    viewingCompany,
    isViewModalOpen,
    setIsViewModalOpen,
    handleOpenModal,
    handleViewDetails,
    handleSubmit,
    handleMultiSelectChange,
    equipmentTypes: EQUIPMENT_TYPES,
    carrierStatuses: CARRIER_STATUSES,
    carrierStatusFilterOptions: CARRIER_STATUS_FILTER_OPTIONS,
    fieldErrors,
    formErrors,
    validate,
    isImportModalOpen,
    setIsImportModalOpen: setImportModalOpen,
    importFile,
    importReport,
    importError,
    importSubmitting: importMutation.isPending,
    templateDownloading: downloadTemplateMutation.isPending,
    guideDownloading: downloadGuideMutation.isPending,
    handleOpenImportModal,
    handleCloseImportModal,
    handleImportFileChange,
    handleDownloadTemplate,
    handleDownloadGuide,
    handleSubmitImport,
  };
};
